import React from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  Button,
  Snackbar,
  Alert,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { MuiFileInput } from "mui-file-input";
import agentService from "../../../services/agent.service";

const AgentImport = () => {
  const [file, setFile] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState(null);
  const [errorMessage, setErrorMessage] = React.useState(null);
  // #100 - per-row failures from the upload (row number, NPN, reason), shown
  // as a persistent report under the form so the sheet can be fixed.
  const [failedRows, setFailedRows] = React.useState([]);

  const handleChange = (newFile) => {
    setFile(newFile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage(null);
    setErrorMessage(null);
    setFailedRows([]);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await agentService.importAgents(formData);
      if (response.status === 201) {
        const { imported, total, created, updated, failed } = response.data;
        const failedCount = failed?.length ?? total - imported;
        setSuccessMessage(
          `Agents imported: ${total} rows, ${updated ?? 0} updated, ${created ?? 0} new, ${failedCount} failed`
        );
        setFailedRows(failed || []);
      } else {
        setErrorMessage(`Something went wrong: ${response.data}`);
      }
    } catch (error) {
      setErrorMessage("Something went wrong: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (_evt, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSuccessMessage(null);
    setErrorMessage(null);
  };

  const action = (
    <>
      <Button color="secondary" size="small" onClick={handleClose}>
        UNDO
      </Button>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleClose}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  return (
    <>
      <Container maxWidth="md">
        <Box sx={{ padding: 3, textAlign: "center" }}>
          <Typography variant="h4" gutterBottom>
            Import agents from a spreadsheet
          </Typography>
          {/* #89 - rows merge into existing agents by NPN; only the columns
              present in the file are updated, so a re-upload can't erase
              fields the sheet doesn't carry. #100 - Upline Agency and FSD
              are validated; a bad value fails that row with a report. */}
          <Typography variant="body1" sx={{ marginBottom: 2 }} gutterBottom>
            Start from the blank template below, or upload a GAIN
            export. Rows are matched to existing agents by NPN, and only the
            columns present in the file are updated. Blank cells never erase
            existing data. Upline Agency must exactly match an agency's name
            and FSD must be a name from the FSD list; a row with a value that
            does not match is skipped and reported below.
          </Typography>
          <div>
            <Button
              component="a"
              href="/api/agent/import-template"
              variant="outlined"
              sx={{ marginBottom: 3 }}
            >
              Download the template
            </Button>
          </div>
          <div>
            <MuiFileInput
              value={file}
              onChange={handleChange}
              variant="filled"
            />
          </div>
          <div>
            <Button
              sx={{ marginTop: "1rem" }}
              onClick={handleSubmit}
              type="submit"
              disabled={loading || !file}
              variant="contained"
            >
              Submit agent spreadsheet
            </Button>
          </div>
          <div>
            {loading && (
              <LinearProgress sx={{ marginTop: "2rem" }} thickness={8} />
            )}
          </div>
          {/* #100 - the row-level error report. Failed rows import NOTHING;
              each line says which row and why so the sheet can be fixed and
              re-uploaded (already-imported rows just merge again harmlessly). */}
          {failedRows.length > 0 && (
            <Alert severity="warning" sx={{ marginTop: 3, textAlign: "left" }}>
              <Typography variant="subtitle2" gutterBottom>
                {failedRows.length} row{failedRows.length === 1 ? "" : "s"} did
                not import. Fix these in the sheet and upload it again:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                {failedRows.map((f) => (
                  <li key={f.row}>
                    Row {f.row}
                    {f.npn ? ` (NPN ${f.npn})` : ""}: {f.reason}
                  </li>
                ))}
              </ul>
            </Alert>
          )}
        </Box>
      </Container>
      {successMessage && (
        <Snackbar
          open={!!successMessage}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{ marginTop: "48px" }}
          onClose={handleClose}
          action={action}
        >
          <Alert
            onClose={handleClose}
            severity="success"
            sx={{ width: "100%" }}
          >
            {successMessage}
          </Alert>
        </Snackbar>
      )}
      {errorMessage && (
        <Snackbar
          open={!!errorMessage}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{ marginTop: "48px" }}
          onClose={handleClose}
          action={action}
        >
          <Alert onClose={handleClose} severity="error" sx={{ width: "100%" }}>
            {errorMessage}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default AgentImport;
